import React, { useState, useMemo } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  ConnectionProvider,
  WalletProvider
} from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { clusterApiUrl } from '@solana/web3.js';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-wallets';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import './DynamicMemberPortal.css';
import Dashboard from './Dashboard/Dashboard';
import Sidebar from './Sidebar/Sidebar';
import Header from './Header/Header';
import { PurchaseModal } from '../PurchaseModal/PurchaseModal';
import { usePurchaseModal } from '../PurchaseModal';
import { ProfileDetailsModal } from '../Home/components/ProfileDetailsModal';
import { isProfileIncomplete } from '../../../utils/profileCompleteness';
import { useUser } from '../../../contexts/UserContext';
import { Subscription } from '../Subscription';
import { UserSettings } from '../UserSettings';
import { Giveaway } from '../Giveaway';
import { WhitePaperDoc } from '../WhitePaperDoc';
import { NFTCollection } from '../NFTCollection';
import PhysicalCards from '../PhysicalCards';
import CardAuction from '../CardAuction/CardAuction';
import { TokenStats } from '../TokenStats';
import { ContactUs } from '../ContactUs';
import { Marketplace } from '../Marketplace/Marketplace';
import MarketplaceNotifications from '../../../components/MarketplaceNotifications';
import DevOrAdminRoute from '../../../auth/DevOrAdminRoute';
import { Roadmap } from '../Roadmap';
import { Leaderboard } from '../Leaderboard';
import ArenaGamePage from '../../TcgArena';
// import KycPromptModal from '../Kyc/KycPromptModal'; // KYC nudge hidden from members until launch
import KycPage from '../Kyc/KycPage';
import { RecoveryCodeSignInNotice } from '../TwoFactorAuth/RecoveryCodeSignInNotice';
import { SecurityHoldNotice } from '../TwoFactorAuth/SecurityHoldNotice';
import { TwoFactorResetRequestNotice } from '../TwoFactorAuth/TwoFactorResetRequestNotice';
import { RecoveryCodesPrompt } from '../TwoFactorAuth/RecoveryCodesPrompt';
import { CardParcelNotice } from '../CardAuction/CardParcelNotice';

type Props = {};

function DynamicMemberPortal({}: Props) {
  const { user } = useUser();
  // A phone should count as "mobile" in BOTH orientations. Width alone missed
  // landscape phones (wide but short), so on rotate they got the desktop layout
  // and the sidebar covered the game. Treat short + not-too-wide viewports
  // (landscape phones, up to ~950px) as mobile too; real tablets/desktops (both
  // dimensions > 600, or wider than 950) stay on the desktop layout.
  const isNarrow = useMediaQuery('(max-width: 600px)');
  const isShortLandscape = useMediaQuery(
    '(max-height: 600px) and (max-width: 950px)'
  );
  const isMobile = isNarrow || isShortLandscape;
  const location = useLocation();
  // On mobile the game needs the whole viewport — the 64px header (and the
  // mobile browser's URL bar) push the game's bottom action buttons out of
  // reach. On the arena page we hide the header bar and let the content fill
  // the visible viewport (100dvh); Header still renders a floating menu button.
  // The game lives at /play; immersive mode hides the header/sidebar there.
  const isGamePage = location.pathname.endsWith('/play');
  // Both arena surfaces — stable /play and HEAD /play-dev — mount a full-bleed
  // iframe sized height:100%. The shared .arena-fade wrapper is height:auto, so
  // without a real height here the whole chain collapses and the game renders at
  // the iframe's intrinsic ~150px. Note /play-dev does NOT end with '/play'.
  const isArenaPage =
    isGamePage || location.pathname.endsWith('/play-dev');
  // …and on /play-dev too (owner 2026-11-05: on a phone the header cost the match its bottom band)
  const immersiveGame = isMobile && isArenaPage;
  /* The page is a column: the header (in flow, whatever its height) and the rest. The rest is
     sized by flex, not by `calc(100vh - 64px)`: a header that comes out taller than 64px in one
     browser or at one zoom level pushed the arena's bottom band under the fold (the owner's
     Firefox at 90%: the fight phase's hand strip was cut off). With flex the arena always gets
     exactly what is left. Immersive (a phone on /play) floats the header, so the column is
     the whole dynamic viewport. */
  const shellHeight = immersiveGame ? '100dvh' : '100vh';

  const network = WalletAdapterNetwork.Mainnet;
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);
  const wallets = useMemo(
    () => [new PhantomWalletAdapter(), new SolflareWalletAdapter()],
    [network]
  );


  /* THE ONE GATE (2026-10-21): a member missing a username, a name, their
     country, state or mobile is held in ProfileDetailsModal — which shows only
     what is missing — until it is saved, before anything else. It replaced the
     separate "choose your username" dialog, so a member a partner (Empire) sent
     us, who arrives with none of these, answers one dialog, not three. */
  const profileRequired = isProfileIncomplete(user);

  const {
    isBuyModalOpen,
    selectedPurchase,
    showFinalUpsell,
    handlePurchaseModalClose,
    handlePurchaseSelection,
    handleBonusPurchaseSuccess
  } = usePurchaseModal();

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          <PurchaseModal
            isModalOpen={!!isBuyModalOpen}
            handlePurchaseModalClose={handlePurchaseModalClose}
            selectedPurchase={selectedPurchase}
            handleBonusPurchaseSuccess={handleBonusPurchaseSuccess}
            showFinalUpsell={showFinalUpsell}
          />
          {/* The giveaway-eligibility question (2026-10-21) is asked inside
              ProfileDetailsModal when the account's answer is unknown; the
              separate GiveawayEligibleModal is no longer mounted here. */}
          {profileRequired && <ProfileDetailsModal open />}
          {/* 2FA on, no recovery codes: asked on arrival, each sign-in, until a set
              exists — after the gates above, and never over the arena. */}
          {!profileRequired && !isArenaPage && <RecoveryCodesPrompt />}
          <MarketplaceNotifications />
          {/* KYC nudge hidden from ALL members until launch — commented out, NOT
              role-gated (a spoofed dev/admin can't reach it). Test on the Dev page. */}
          {/* {(user.role === 'dev' || user.role === 'admin') && <KycPromptModal />} */}
          <Box sx={{ width: '100%', height: shellHeight, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                zIndex: -2,
                background:
                  'radial-gradient(1200px 700px at 15% -10%, #0e1c28 0%, transparent 55%), radial-gradient(1000px 600px at 100% 0%, #0a1a1f 0%, transparent 50%), #04070b'
              }}
            />

            <Header isMobile={isMobile} floating={immersiveGame} />

            <Box
              className="container"
              sx={{
                flex: '1 1 auto',
                minHeight: 0,
                width: '100%',
                display: 'flex',
                overflow: 'hidden'
              }}
            >
              {!isMobile && <Sidebar />}

              <Box
                className="content"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  width: '100%',
                  paddingRight: 0,
                  background: 'rgba(0, 0, 0, 0.2)',
                  /* a stretched flex item: definite height, so the arena's height:100% chain
                     (.arena-fade--game → ArenaGamePage → the iframe) resolves in every engine */
                  alignSelf: 'stretch',
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }}
              >
                {/* After a sign-in on a recovery code, above every page until closed —
                    but not the arena, which needs the content's whole height. */}
                {!isArenaPage && <RecoveryCodeSignInNotice />}
                {!isArenaPage && <TwoFactorResetRequestNotice />}
                {!isArenaPage && <SecurityHoldNotice />}
                {!isArenaPage && <CardParcelNotice />}
                <div
                  key={location.pathname}
                  className={
                    isArenaPage ? 'arena-fade arena-fade--game' : 'arena-fade'
                  }
                >
                <Routes>
                  <Route
                    path="/"
                    element={
                      <Dashboard
                        handlePurchaseSelection={handlePurchaseSelection}
                      />
                    }
                  />
                  {/* Duel Arena — live for all logged-in members. Loads the
                      PROMOTED stable snapshot (public/tcg-arena/stable), not HEAD,
                      so the latest arena build can be tested on /play-dev first and
                      promoted (scripts/promote-arena.js) when it's good. */}
                  <Route
                    path="/play"
                    element={
                      <ArenaGamePage
                        embedded
                        requireAdmin={false}
                        arenaUrl="/tcg-arena/stable/index.html"
                      />
                    }
                  />
                  <Route
                    path="/subscription"
                    element={
                      <Subscription
                        handlePurchaseSelection={handlePurchaseSelection}
                      />
                    }
                  />
                  <Route path="/settings" element={<UserSettings />} />
                  <Route
                    path="/giveaway"
                    element={
                      <Giveaway
                        handlePurchaseSelection={handlePurchaseSelection}
                      />
                    }
                  />
                  <Route path="/white-paper" element={<WhitePaperDoc />} />
                  <Route path="/roadmap" element={<Roadmap />} />
                  <Route path="/Leaderboard" element={<Leaderboard />} />
                  <Route path="/contact" element={<ContactUs />} />
                  <Route
                    path="/digital-collectibles"
                    element={<NFTCollection />}
                  />
                  {/* Old /member-portal/nft — keep it working for existing
                      bookmarks/links after the rename away from "nft". */}
                  <Route
                    path="/nft"
                    element={
                      <Navigate
                        to="/member-portal/digital-collectibles"
                        replace
                      />
                    }
                  />

                  <Route path="/marketplace" element={<Marketplace />} />

                  <Route path="/token" element={<TokenStats />} />

                  {/* Physical trading-card PRE-PURCHASE storefront (Angelic Faction),
                      open to every signed-in member. */}
                  <Route path="/physical-cards" element={<PhysicalCards />} />

                  {/* The Incrypt Break (the card auction, lib/cardAuction.js on the server): the live
                      pack opening of the physical Angelic set. Renamed 2026-11-04; the old address still lands. */}
                  <Route path="/incrypt-break" element={<CardAuction />} />
                  <Route path="/card-auction" element={<Navigate to="/member-portal/incrypt-break" replace />} />

                  <Route path="/kyc" element={<KycPage />} />

                  <Route element={<DevOrAdminRoute user={user} />}>
                    {/* Dev-only Duel Arena surface. Loads the HEAD arena build
                        (public/tcg-arena) with a DEV badge so the latest ruleset
                        can be played on the live site before it's promoted to the
                        member-facing /play page. Promote: scripts/promote-arena.js */}
                    <Route
                      path="/play-dev"
                      element={
                        <ArenaGamePage embedded requireAdmin={false} devTag />
                      }
                    />
                  </Route>
                </Routes>
                </div>
              </Box>
            </Box>
          </Box>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}

export default DynamicMemberPortal;
